import { Directive, ElementRef, HostListener, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
    selector: 'input[appPhoneFormat]',
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneFormatDirective),
            multi: true
        }
    ]
})
export class PhoneFormatDirective implements ControlValueAccessor, OnInit {
    private onChange: (value: string | null) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private el: ElementRef<HTMLInputElement>) {}

    ngOnInit(): void {
        this.el.nativeElement.type = 'tel';
        this.el.nativeElement.maxLength = 14; // (555) 555-5555
    }

    writeValue(value: string | null): void {
        if (!value) {
            this.el.nativeElement.value = '';
        } else {
            this.el.nativeElement.value = this.formatPhone(this.stripNonDigits(value));
        }
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    @HostListener('input')
    onInput(): void {
        const input = this.el.nativeElement;
        const digits = this.stripNonDigits(input.value).substring(0, 10);
        const formatted = this.formatPhone(digits);
        input.value = formatted;
        this.onChange(digits || null);
    }

    @HostListener('blur')
    onBlur(): void {
        this.onTouched();
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        const allowed = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];
        if (allowed.includes(event.key)) return;

        if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) return;

        if (event.key >= '0' && event.key <= '9') {
            const digits = this.stripNonDigits(this.el.nativeElement.value);
            if (digits.length >= 10) {
                event.preventDefault();
            }
            return;
        }

        event.preventDefault();
    }

    private stripNonDigits(value: string): string {
        return value.replace(/\D/g, '');
    }

    private formatPhone(digits: string): string {
        if (digits.length === 0) return '';
        if (digits.length <= 3) return `(${digits}`;
        if (digits.length <= 6) return `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
        return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
    }
}
